import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import {
    Image,
    Menu,
    MenuButton,
    MenuButtonProps,
    MenuItem,
    MenuList,
    Portal,
    useDisclosure
} from '@chakra-ui/react';
import {
    ChevronRightIcon,
    CopyIcon,
    DotsIcons,
    DuplicateIcon,
    ExternalLinkIcon,
    MotionBox,
    PlusCircleIcon,
    TrashIcon,
    ChevronClose,
    ArrowCircleDownIcon
} from './UiElements';
import { Button, IconButton } from '@chakra-ui/button';
import { forwardRef, useMemo, useState } from 'react';
import OpenSitesDialog from './AddSiteModal';
import { motion, AnimatePresence } from 'framer-motion';
import useStore from '../stores/store';
import {
    AnimatedMoveRow,
    MoveGroupToAccordion,
    SortAlaphabeticalIcon,
    SortByIcon,
    TwoArrowsIcon
} from './MoveAccordion';
import { HoverMenu, SubMenuButton, SubMenuList } from './SubMenu';
import usePersistStore from '../stores/persistStore';

const GroupMenu = ({ groupId, index }) => {
    const [addSiteModalOpen, setAddSiteModal] = useState(false);
    const openAddSiteModal = () => setAddSiteModal(true);
    const buttonHover = useColorModeValue('gray.50', '#32363A');
    const { isOpen, onOpen, onClose } = useDisclosure();

    const menuBg = useColorModeValue('#FFF', '#262A2D');
    const lightBg = useColorModeValue('gray.50', '#31363a');
    const deleteBg = useColorModeValue('red.100', '#552F32');

    const menuProps = {
        bg: { menuBg },
        p: '8px 12px',
        rounded: '6px',
        fontWeight: 500,
        transition: '0.05s',
        //margin: '4px',
        _hover: { bg: lightBg },
        iconSpacing: '12px'
    };

    const minimizeGroup = () => {
        useStore.getState().toggleMinimizeGroup(groupId, true);
    };

    return (
        <>
            {/* <IconButton
                aria-label="Toggle shelf-view"
                icon={<ChevronClose />}
                bg="transparent"
                h="32px"
                w="32px"
                minW="32px"
                rounded="7px"
                p="0"
                alignItems={'center'}
                transition={'0.16s'}
                ml="auto"
                mr="4px"
                mb="auto"
                opacity={0}
                _hover={{
                    bg: buttonHover
                }}
                _active={{
                    bg: buttonHover
                }}
                transform={'translateX(6px)'}
                _groupHover={{
                    transform: 'translateX(0px)',
                    opacity: 1
                }}
            /> */}
            <Menu
                isLazy
                placement="bottom-end"
                isOpen={isOpen}
                onClose={onClose}
            >
                {({ isOpen }) => (
                    <>
                        <MenuButton
                            as={IconButton}
                            aria-label="Options"
                            icon={<DotsIcons />}
                            bg="transparent"
                            h="32px"
                            w="32px"
                            minW="32px"
                            rounded="7px"
                            p="0"
                            alignItems={'center'}
                            transition={'0.16s'}
                            mr="0px"
                            ml="auto"
                            mb="auto"
                            opacity={isOpen ? 0.5 : 0}
                            _hover={{
                                bg: buttonHover
                            }}
                            _active={{
                                bg: buttonHover
                            }}
                            transform={
                                isOpen ? 'translateX(0px)' : 'translateX(6px)'
                            }
                            _groupHover={{
                                transform: 'translateX(0px)',
                                opacity: isOpen ? 0.5 : 1
                            }}
                            onClick={onOpen}
                        />
                        <AnimatePresence>
                            {isOpen && (
                                <MenuList
                                    as={motion.div}
                                    initial={{
                                        y: -8,
                                        opacity: 0.4
                                    }}
                                    animate={{
                                        y: 0,
                                        opacity: 1,
                                        transition: {
                                            duration: 0.12,
                                            ease: [0.17, 0.67, 0.83, 0.67]
                                        }
                                    }}
                                    fontSize={'15px'}
                                    p="4px"
                                    border="none"
                                    rounded="8px"
                                    boxShadow={
                                        'var(--chakra-colors-popoverShadowLight)'
                                    }
                                    bg={menuBg}
                                    zIndex={999999999}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <MenuItem
                                        icon={<ChevronClose />}
                                        /* command="⌘G" */
                                        {...menuProps}
                                        onClick={minimizeGroup}
                                    >
                                        Minimize
                                    </MenuItem>
                                    {/* <MenuItem
                                        icon={<ExternalLinkIcon />}
                                        {...menuProps}
                                    >
                                        Open group
                                    </MenuItem> */}
                                    <MenuItem
                                        icon={<PlusCircleIcon />}
                                        {...menuProps}
                                        onClick={openAddSiteModal}
                                    >
                                        Add site/image
                                    </MenuItem>
                                    {/* <MoveAccordion /> */}
                                    <MenuItem
                                        groupId={groupId}
                                        as={SubMenu as React.ElementType}
                                        index={index}
                                        closeGroupMenu={onClose}
                                    />
                                    <MenuItem
                                        as={
                                            SortGroupSubMenu as React.ElementType
                                        }
                                        index={index}
                                        groupId={groupId}
                                        closeGroupMenu={onClose}
                                    />
                                    <MenuItem
                                        onClick={() =>
                                            useStore
                                                .getState()
                                                .duplicateGroup(groupId)
                                        }
                                        icon={<DuplicateIcon />}
                                        {...menuProps}
                                    >
                                        Duplicate group
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() =>
                                            useStore
                                                .getState()
                                                .copyContent(groupId)
                                        }
                                        icon={<CopyIcon />}
                                        {...menuProps}
                                    >
                                        Copy as text
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() =>
                                            useStore
                                                .getState()
                                                .deleteGroup(groupId)
                                        }
                                        color="red.500"
                                        icon={
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="#ea5b5b"
                                                style={{
                                                    width: '16px',
                                                    height: '16px'
                                                }}
                                            >
                                                <path
                                                    d="M9 2C8.62123 2 8.27497 2.214 8.10557 2.55279L7.38197 4H4C3.44772 4 3 4.44772 3 5C3 5.55228 3.44772 6 4 6L4 16C4 17.1046 4.89543 18 6 18H14C15.1046 18 16 17.1046 16 16V6C16.5523 6 17 5.55228 17 5C17 4.44772 16.5523 4 16 4H12.618L11.8944 2.55279C11.725 2.214 11.3788 2 11 2H9ZM7 8C7 7.44772 7.44772 7 8 7C8.55228 7 9 7.44772 9 8V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V8ZM12 7C11.4477 7 11 7.44772 11 8V14C11 14.5523 11.4477 15 12 15C12.5523 15 13 14.5523 13 14V8C13 7.44772 12.5523 7 12 7Z"
                                                    fillRule="evenodd"
                                                />
                                            </svg>
                                        }
                                        {...menuProps}
                                        _hover={{
                                            bg: deleteBg,
                                            filter: 'brightness(1.1)'
                                        }}
                                    >
                                        Delete
                                    </MenuItem>
                                </MenuList>
                            )}
                        </AnimatePresence>
                    </>
                )}
            </Menu>
            {addSiteModalOpen && (
                <OpenSitesDialog
                    closeSiteDialog={() => setAddSiteModal(false)}
                    groupId={groupId}
                />
            )}
        </>
    );
};

export default GroupMenu;

const SubMenu = forwardRef<MenuButtonProps, 'button'>((props, ref) => {
    const groupsToShow = useStore((state) => state.groupsToShow);
    const menuBg = useColorModeValue('#FFF', '#262A2D');
    const lightBg = useColorModeValue('gray.50', '#31363a');
    const deleteBg = useColorModeValue('red.100', '#552F32');

    const menuProps = {
        bg: { menuBg },
        p: '8px 12px',
        rounded: '6px',
        fontWeight: 500,
        transition: '0.05s',
        //margin: '4px',
        _hover: { bg: lightBg },
        iconSpacing: '12px'
    };
    let currentCatLength = useStore.getState().groupsToShow.length;

    let isLast =
        //@ts-ignore
        props.index === currentCatLength - 1 && groupsToShow.length !== 1;
    return (
        <HoverMenu /* closeOnSelect={false} */ isLast={isLast}>
            <div>
                <SubMenuButton
                    //@ts-ignore
                    ref={ref}
                    //@ts-ignore
                    {...menuProps}
                    //{...props}
                    icon={<TwoArrowsIcon />}
                    command={<ChevronRightIcon />}
                >
                    Move to
                </SubMenuButton>
            </div>

            <SubMenuList
                mt="138px"
                fontSize={'15px'}
                p="4px"
                border="none"
                rounded="8px"
                boxShadow={'var(--chakra-colors-popoverShadowLight)'}
                bg={menuBg}
                zIndex={999999999}
                maxW="194px"
            >
                <MoveGroupToAccordion
                    //@ts-ignore
                    groupId={props.groupId}
                    //@ts-ignore
                    closeGroupMenu={props.closeGroupMenu}
                />
            </SubMenuList>
        </HoverMenu>
    );
});

const SortGroupSubMenu = forwardRef<MenuButtonProps, 'button'>((props, ref) => {
    const groupsToShow = useStore((state) => state.groupsToShow);
    const menuBg = useColorModeValue('#FFF', '#262A2D');
    const lightBg = useColorModeValue('gray.50', '#31363a');

    const menuProps = {
        bg: { menuBg },
        p: '8px 12px',
        rounded: '6px',
        fontWeight: 500,
        transition: '0.05s',
        //margin: '4px',
        _hover: { bg: lightBg },
        iconSpacing: '12px'
    };

    const [menuOpen, setMenuOpen] = useState(null);
    let currentCatLength = useStore.getState().groupsToShow.length;

    let isLast =
        //@ts-ignore
        props.index === currentCatLength - 1 && groupsToShow.length !== 1;

    const sortByAlaphabetical = () => {
        //@ts-ignore
        useStore.getState().sortGroupAlphabetically(props.groupId);
        //@ts-ignore

        props.closeGroupMenu();
    };
    const sortByTime = () => {
        //console.log('Sort by time');
        //@ts-ignore
        useStore.getState().sortGroupByDate(props.groupId);
        //@ts-ignore

        props.closeGroupMenu();
    };

    return (
        <HoverMenu /* closeOnSelect={false} */ isLast={isLast}>
            <div>
                <SubMenuButton
                    //@ts-ignore
                    ref={ref}
                    //@ts-ignore
                    {...menuProps}
                    //{...props}
                    icon={<SortByIcon />}
                    command={<ChevronRightIcon />}
                >
                    Sort by
                </SubMenuButton>
            </div>
            <SubMenuList
                mt="176px"
                fontSize={'15px'}
                p="4px"
                border="none"
                rounded="8px"
                boxShadow={'var(--chakra-colors-popoverShadowLight)'}
                bg={menuBg}
                zIndex={999999999}
                maxW="194px"
            >
                <MenuItem
                    {...menuProps}
                    icon={<SortAlaphabeticalIcon />}
                    onClick={sortByAlaphabetical}
                >
                    Alphabetical
                </MenuItem>
                <MenuItem
                    {...menuProps}
                    icon={<ArrowCircleDownIcon size="16px" />}
                    onClick={sortByTime}
                >
                    Date added
                </MenuItem>
            </SubMenuList>
        </HoverMenu>
    );
});
